import {
  longApiDate,
  type NextPaycheck,
  type Paycheck,
  type PaycheckFrequency,
  type PaycheckPeriod,
  type PaycheckPeriodSource,
} from "@fomofi/shared";
import apiClient from "./client";

/* ─────────────────────── API response types (snake_case) ───────────────────────
 * Mirrors backend/apps/forecasting/serializers.py (PaycheckSerializer).
 * Money values arrive as JS numbers (FloatField); dates as ISO strings. */

export interface ApiNextPaycheck {
  date: string;
  day_of_week: string;
  days_until: number;
  net_amount: number;
  account_name: string | null;
  account_mask: string | null;
  /* BEN-141 3b. Optional on the wire so a frontend deploy that lands before the
   * backend one degrades to "not editable" instead of crashing on a missing pk. */
  recurring_id?: number;
  frequency?: PaycheckFrequency;
  anchors_pay_periods?: boolean;
  /* BEN-141 P31. Optional for the same deploy-order reason: an older backend
   * simply has not been told about deductions yet, which is the same state as a
   * user who has not confirmed them. */
  gross_amount?: number | null;
  deductions_amount?: number | null;
  deductions_confirmed?: boolean;
}

export interface ApiPaycheckPeriod {
  label: string;
  start_date: string;
  end_date: string;
  income: number;
  expenses: number;
  remainder: number;
  is_current: boolean;
  source?: PaycheckPeriodSource;
}

export interface ApiPaycheck {
  next_paycheck: ApiNextPaycheck | null;
  periods: ApiPaycheckPeriod[];
}

/* ─────────────────────────────── adapters ─────────────────────────────── */

function toNextPaycheck(n: ApiNextPaycheck): NextPaycheck {
  return {
    // "Sun, Aug 9, 2026", not the wire's "2026-08-09" (BEN-141 2f). The Dashboard
    // card printed the raw ISO string next to `day_of_week`, giving "Sunday,
    // 2026-08-09" for the paycheck Forecast rendered as "Sun, Aug 9, 2026" —
    // both from this backend, one click apart. Same helper as Forecast now.
    date: longApiDate(n.date),
    dateISO: n.date,
    dayOfWeek: n.day_of_week,
    daysUntil: n.days_until,
    netAmount: n.net_amount,
    accountName: n.account_name,
    accountMask: n.account_mask,
    // Null (not 0) when absent, so the confirm step renders read-only rather than
    // wiring an edit control to a row that does not exist. See NextPaycheck.
    recurringId: n.recurring_id ?? null,
    frequency: n.frequency ?? null,
    // Defaults FALSE when absent: claiming an edit will move the user's pay period is
    // the more damaging of the two possible wrong answers.
    anchorsPayPeriods: n.anchors_pay_periods ?? false,
    // Null, never 0. "We have not been told" and "there are no deductions" are
    // different answers and the card says different things about them.
    grossAmount: n.gross_amount ?? null,
    deductionsAmount: n.deductions_amount ?? null,
    deductionsConfirmed: n.deductions_confirmed ?? false,
  };
}

function toPaycheckPeriod(p: ApiPaycheckPeriod): PaycheckPeriod {
  return {
    label: p.label,
    startDate: p.start_date,
    endDate: p.end_date,
    income: p.income,
    expenses: p.expenses,
    remainder: p.remainder,
    isCurrent: p.is_current,
    // Defaults to "live", which is what an older backend was doing for every
    // period. Claiming a figure was recorded when it was derived is the more
    // damaging of the two wrong answers here.
    source: p.source ?? "live",
  };
}

export function toPaycheck(data: ApiPaycheck): Paycheck {
  return {
    nextPaycheck: data.next_paycheck ? toNextPaycheck(data.next_paycheck) : null,
    periods: data.periods.map(toPaycheckPeriod),
  };
}

/** Next paycheck + per-pay-period income/expense history (#384). */
export async function getPaycheck(count = 4): Promise<Paycheck> {
  const { data } = await apiClient.get<ApiPaycheck>(`/forecasting/paycheck/?count=${count}`);
  return toPaycheck(data);
}
